import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { Meter } from "@/components/dashboard/widgets/Meter";
import { SERIES_BG, SERIES_BG_SOFT } from "@/components/dashboard/widgets/opsColors";
import { fmtCount } from "./listMath";

export interface ListTileSpec {
  key: string;
  label: string;
  value: number;
  sub: string;
  /** The covered part in SERIES_BG against the rest in SERIES_BG_SOFT. */
  coverage?: { covered: number; total: number; title: string };
  /** A clickable tile applies its filter. */
  onClick?: () => void;
  active?: boolean;
}

function Tile({ t }: { t: ListTileSpec }) {
  const body = (
    <>
      <span className="shrink-0 text-[11px] font-medium text-muted-foreground truncate">{t.label}</span>
      <span className="shrink-0 text-xl font-semibold tabular-nums leading-tight">{fmtCount(t.value)}</span>
      {t.coverage && (
        <Meter
          title={t.coverage.title}
          segments={[
            { value: t.coverage.covered, className: SERIES_BG },
            { value: Math.max(0, t.coverage.total - t.coverage.covered), className: SERIES_BG_SOFT },
          ]}
        />
      )}
      <span className="text-[11px] text-muted-foreground truncate" title={t.sub}>{t.sub}</span>
    </>
  );
  const cls = cn(
    "min-w-0 bg-card border rounded-lg px-3 py-1.5 flex flex-col text-left overflow-hidden",
    t.active ? "border-primary ring-1 ring-primary" : "border-border",
    t.onClick && "hover:border-foreground/30 transition-colors",
  );
  return t.onClick
    ? <button type="button" aria-pressed={!!t.active} onClick={t.onClick} className={cls} data-testid={`tile-${t.key}`}>{body}</button>
    : <div className={cls} data-testid={`tile-${t.key}`}>{body}</div>;
}

/** The tile row across the top of a list page: 2 per row below lg, all in one row at lg. */
export function ListTiles({ tiles }: { tiles: ListTileSpec[] }) {
  return (
    <div
      className="grid grid-cols-2 gap-2 lg:grid-cols-[repeat(var(--tiles),minmax(0,1fr))]"
      style={{ "--tiles": tiles.length } as CSSProperties}
    >
      {tiles.map((t) => <Tile key={t.key} t={t} />)}
    </div>
  );
}
